import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AccountBalance, TrialBalanceRow } from '../lib/types';
import {
  Card,
  EmptyState,
  ErrorNote,
  Money,
  PageHead,
  Spinner,
  Tag,
} from '../components/ui';

export function LedgerPage() {
  const trial = useQuery({
    queryKey: ['trial-balance'],
    queryFn: () => api.get<TrialBalanceRow[]>('/ledger/trial-balance'),
  });

  const balances = useQuery({
    queryKey: ['account-balances'],
    queryFn: () => api.get<AccountBalance[]>('/ledger/balances'),
  });

  const allBalanced = trial.data?.every((r) => r.balanced) ?? true;

  return (
    <>
      <PageHead
        crumb="Books"
        title="Ledger"
        sub="Every figure below is derived from posted double-entry, not from a status field."
      />

      <ErrorNote error={trial.error ?? balances.error} />

      <Card
        title="Trial balance"
        actions={
          trial.data ? (
            <Tag tone={allBalanced ? 'positive' : 'danger'}>
              {allBalanced ? 'In balance' : 'OUT OF BALANCE'}
            </Tag>
          ) : null
        }
        padded={false}
      >
        {trial.isLoading ? (
          <Spinner />
        ) : !trial.data?.length ? (
          <EmptyState title="Nothing posted yet" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Currency</th>
                  <th className="r">Debits</th>
                  <th className="r">Credits</th>
                  <th className="r">Difference</th>
                  <th className="r">Entries</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {trial.data.map((r) => (
                  <tr key={r.currency} style={{ cursor: 'default' }}>
                    <td className="cell-t1">{r.currency}</td>
                    <td className="r">
                      <Money minor={r.debitMinor} currency={r.currency} />
                    </td>
                    <td className="r">
                      <Money minor={r.creditMinor} currency={r.currency} />
                    </td>
                    <td className="r">
                      <Money minor={r.differenceMinor} currency={r.currency} />
                    </td>
                    <td className="r num">{r.entryCount}</td>
                    <td>
                      <Tag tone={r.balanced ? 'positive' : 'danger'}>
                        {r.balanced ? 'balanced' : 'broken'}
                      </Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="section-title">Account balances</div>

      <Card padded={false}>
        {balances.isLoading ? (
          <Spinner />
        ) : !balances.data?.length ? (
          <EmptyState title="No accounts have entries yet" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Currency</th>
                  <th className="r">Balance</th>
                  <th className="r">Entries</th>
                </tr>
              </thead>
              <tbody>
                {balances.data.map((a) => (
                  <tr key={a.account_id} style={{ cursor: 'default' }}>
                    <td className="num">{a.code}</td>
                    <td>
                      <span className="cell-t1">{a.name}</span>
                      {a.party_id ? (
                        <span className="cell-t2" style={{ display: 'block' }}>
                          party sub-ledger
                        </span>
                      ) : null}
                    </td>
                    <td className="cell-t2">{a.type}</td>
                    <td className="cell-t2">{a.currency}</td>
                    <td className="r">
                      <Money minor={a.balance_minor} currency={a.currency} />
                    </td>
                    <td className="r num">{a.entry_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
